import { SearchOutlined } from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import { Col, Form, Row, Select, Typography } from 'antd';
import { ScopeTabs } from 'components/Custom/tabs';
import { ScopeCheckbox } from 'components/Custom/checkbox';
import {
  buildMembersFromEntityList,
  GroupConnectedEntitiesList,
} from 'components/CreateGroup/GroupConnectedEntitiesList';
import { ProviderIcon } from 'components/Custom/ProviderIcon';
import { FormInput, RoundInput } from 'components/Custom/inputs';
import { LabelWrapper } from 'components/Custom/labels';
import { FormSelect } from 'components/Custom/selects';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountryLabel } from 'hooks/useCountryLabel';
import { useDispatch, useSelector } from 'react-redux';
import { turnDrawerOff } from 'store/app/actions';
import {
  fetchCountriesExternalDataRequest,
  fetchCountriesRequest,
  fetchCurrenciesRequest,
} from 'store/common/actions';
import { Country } from 'store/common/models';
import {
  getCommonCountries,
  getCommonCurrencies,
  getCountriesExternalData,
  getCurrenciesLoaded,
} from 'store/common/selectors';
import {
  createEntityGroupRequest,
  updateEntityGroupRequest,
} from 'store/organisation/actions';
import { Entity } from 'store/organisation/models';
import styled from 'styled-components';

const { Text } = Typography;

const EntityListContainer = styled.div`
  border: 1px solid var(--color-border, #dbeae3);
  border-radius: 5px;
  background: var(--color-surface-raised, #ffffff);
  padding: 8px;
`;

const EntityListScrollArea = styled.div`
  height: calc(100vh - 630px);
  overflow-x: hidden;
  overflow-y: auto;
`;

const EntityItem = styled.div<{ $isSelected?: boolean }>`
  display: flex;
  align-items: center;
  min-width: 0;
  padding: 4px 10px;
  border-radius: 5px;
  margin-bottom: 4px;
  background: ${(props) => (props.$isSelected ? 'var(--color-layout-bg, #E9F6F7)' : 'transparent')};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: var(--color-layout-bg, #e9f6f7);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const EntityInfo = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  margin-left: 0;
  gap: 10px;
  overflow: hidden;
`;

const EntityDetails = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

/**
 * Entity name column: cap width for alignment, never wider than the row (avoids horizontal scroll).
 * Full name on hover via Typography ellipsis tooltip.
 */
const EntityNameBlock = styled.div`
  min-width: 0;
  width: 100%;
  max-width: min(220px, 100%);
  overflow: hidden;
`;

const SelectedCount = styled.div`
  font-size: 12px;
  color: var(--color-text-muted, #5a6a6a);
  margin-top: 8px;
`;

const EntityError = styled.div<{ $visible: boolean }>`
  font-size: 12px;
  color: #d13131;
  margin-top: 4px;
  min-height: 18px;
  visibility: ${(props) => (props.$visible ? 'visible' : 'hidden')};
`;

type EntityWithGroupedConnections = Entity & {
  groupedConnections?: { uuid: string }[];
};

function getGroupMemberConnectionIds(group: Entity): string[] {
  const fromIds = group.groupedEntityIds?.filter(Boolean) ?? [];
  if (fromIds.length > 0) {
    return fromIds;
  }
  return (
    (group as EntityWithGroupedConnections).groupedConnections
      ?.map((c) => c.uuid)
      .filter(Boolean) ?? []
  );
}

interface CreateGroupProps {
  entities: Entity[];
  editGroup?: Entity;
}

type FieldType = {
  groupName?: string;
  country?: string;
  currency?: string;
  vatNumber?: string;
};

interface GroupCountry {
  countryCode: string;
  countryName: string;
  iso?: string;
  flag?: string;
}

const MIN_ENTITIES = 2;
const MAX_ENTITIES = 10;
const COUNTRY_NAME_MAX_DISPLAY = 10;

export const CreateGroup: React.FC<CreateGroupProps> = ({
  entities,
  editGroup,
}) => {
  const { t } = useTranslation();
  const { localizeCountry } = useCountryLabel();
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const currencies = useSelector(getCommonCurrencies);
  const currenciesLoaded = useSelector(getCurrenciesLoaded);
  const commonCountries = useSelector(getCommonCountries);
  const countriesExternalData = useSelector(getCountriesExternalData);

  const [selectedEntities, setSelectedEntities] = useState<string[]>([]);
  const [countries, setCountries] = useState<GroupCountry[]>([]);
  const [entityError, setEntityError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Sort entities alphabetically by organisation name.
  const sortedEntities = useMemo(() => {
    const list = entities.filter(
      (entity) => entity.providerName?.toLowerCase() !== 'group'
    );
    list.sort((a, b) => {
      return a.organisation.localeCompare(b.organisation);
    });
    return list;
  }, [entities]);

  // Filter entities based on search term
  const filteredEntities = useMemo(() => {
    if (!searchTerm.trim()) {
      return sortedEntities;
    }
    const search = searchTerm.toLowerCase().trim();
    return sortedEntities.filter(
      (entity) =>
        entity.organisation.toLowerCase().includes(search) ||
        (entity.vatNumber && entity.vatNumber.toLowerCase().includes(search))
    );
  }, [sortedEntities, searchTerm]);

  const availableCurrencies = useMemo(() => {
    if (selectedEntities.length < MIN_ENTITIES) return [];

    const selectedEntitySet = new Set(selectedEntities);
    return entities
      .filter((entity) => selectedEntitySet.has(entity.organisationId))
      .map((entity) => entity.currency)
      .filter((currency): currency is string => Boolean(currency))
      .filter((currency, index, arr) => arr.indexOf(currency) === index)
      .sort((a, b) => a.localeCompare(b));
  }, [entities, selectedEntities]);

  const availableCountries = useMemo(() => {
    if (selectedEntities.length < MIN_ENTITIES) return [];

    const selectedEntitySet = new Set(selectedEntities);
    const selectedCountryKeys = new Set(
      entities
        .filter((entity) => selectedEntitySet.has(entity.organisationId))
        .map((entity) => (entity.country || '').trim().toLowerCase())
        .filter(Boolean)
    );

    return countries.filter((country) => {
      const keys = [country.countryCode, country.iso, country.countryName]
        .map((value) => (value || '').trim().toLowerCase())
        .filter(Boolean);

      return keys.some((key) => selectedCountryKeys.has(key));
    });
  }, [countries, entities, selectedEntities]);

  // Fetch currencies if not already loaded (same source as add/edit entity flow)
  useEffect(() => {
    if (currenciesLoaded) return;
    dispatch(fetchCurrenciesRequest());
  }, [dispatch, currenciesLoaded]);

  useEffect(() => {
    const currentCurrency = form.getFieldValue('currency');
    if (selectedEntities.length < MIN_ENTITIES) {
      if (currentCurrency) {
        form.setFieldsValue({ currency: undefined });
      }
      return;
    }

    if (availableCurrencies.length === 1) {
      if (currentCurrency !== availableCurrencies[0]) {
        form.setFieldsValue({ currency: availableCurrencies[0] });
      }
      return;
    }

    if (currentCurrency && !availableCurrencies.includes(currentCurrency)) {
      form.setFieldsValue({ currency: undefined });
    }
  }, [availableCurrencies, form, selectedEntities.length]);

  useEffect(() => {
    const currentCountry = form.getFieldValue('country');
    if (selectedEntities.length < MIN_ENTITIES) {
      if (currentCountry) {
        form.setFieldsValue({ country: undefined });
      }
      return;
    }

    if (availableCountries.length === 1) {
      if (currentCountry !== availableCountries[0].countryCode) {
        form.setFieldsValue({ country: availableCountries[0].countryCode });
      }
      return;
    }

    if (
      currentCountry &&
      !availableCountries.some(
        (country) => country.countryCode === currentCountry
      )
    ) {
      form.setFieldsValue({ country: undefined });
    }
  }, [availableCountries, form, selectedEntities.length]);

  useEffect(() => {
    if (!editGroup) return;

    const selected = getGroupMemberConnectionIds(editGroup);

    setSelectedEntities(selected);
    setEntityError(validateEntitySelection(selected));
    form.setFieldsValue({
      groupName: editGroup.organisation,
      country: editGroup.country,
      currency: editGroup.currency,
      vatNumber: editGroup.vatNumber,
    });
  }, [editGroup]);

  // Fetch countries and flag data so country dropdown can show flags (same as add/edit entity flow)
  useEffect(() => {
    if (!commonCountries || commonCountries.length === 0) {
      dispatch(fetchCountriesRequest());
    }
  }, [dispatch, commonCountries]);

  useEffect(() => {
    if (
      commonCountries &&
      commonCountries.length > 0 &&
      (!countriesExternalData || countriesExternalData.length === 0)
    ) {
      dispatch(fetchCountriesExternalDataRequest());
    }
  }, [dispatch, commonCountries, countriesExternalData]);

  // Build countries from common reference data so selection is not limited by
  // return types fetched for a single connection.
  useEffect(() => {
    if (!commonCountries || commonCountries.length === 0) {
      setCountries([]);
      return;
    }

    const sortedCountries = [...commonCountries]
      .map((country: Country) => {
        const iso = (country.iso || '').toUpperCase();
        const flag =
          countriesExternalData?.find(
            (f) => f.cca2?.toLowerCase() === country.iso?.toLowerCase()
          )?.flags?.png ?? country.flag;
        return {
          countryCode: iso,
          countryName: country.name,
          iso,
          flag,
        } as GroupCountry;
      })
      .filter((country) => Boolean(country.countryCode))
      .sort((a, b) => a.countryName.localeCompare(b.countryName));

    setCountries(sortedCountries);
  }, [commonCountries, countriesExternalData]);

  const onCountryChange = (countryCode: string) => {
    form.setFieldsValue({ country: countryCode });
    form.validateFields(['country']);
  };

  const onCurrencyChange = (currency: string) => {
    form.setFieldsValue({ currency });
    form.validateFields(['currency']);
  };

  const getCountryDisplayText = (country: GroupCountry): string => {
    const localizedName = localizeCountry(
      country.iso || country.countryCode,
      country.countryName
    );
    if (localizedName.length > COUNTRY_NAME_MAX_DISPLAY) {
      return (
        country.iso ||
        country.countryCode ||
        localizedName
      ).toUpperCase();
    }
    return localizedName;
  };

  const getCountrySearchLabel = (country: GroupCountry): string => {
    const localizedName = localizeCountry(
      country.iso || country.countryCode,
      country.countryName
    );
    return `${localizedName} ${(
      country.iso ||
      country.countryCode ||
      ''
    ).toUpperCase()}`.trim();
  };

  const renderCountryWithFlag = (country: GroupCountry): React.ReactNode => (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        verticalAlign: 'middle',
        gap: 8,
      }}
    >
      {country.flag ? (
        <img
          src={country.flag}
          alt={country.countryName}
          width={24}
          height={16}
          style={{
            borderRadius: 2,
            flexShrink: 0,
            display: 'block',
          }}
        />
      ) : (
        <span
          style={{
            width: 24,
            height: 16,
            borderRadius: 2,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 9,
            fontWeight: 600,
            backgroundColor: '#e8e8e8',
            color: '#666',
            flexShrink: 0,
          }}
        >
          {(country.iso || country.countryCode || '?').toUpperCase()}
        </span>
      )}
      <span style={{ fontWeight: 'normal', lineHeight: 1 }}>
        {getCountryDisplayText(country)}
      </span>
    </span>
  );

  // Validate entity selection
  const validateEntitySelection = (selected: string[]): string => {
    if (selected.length < MIN_ENTITIES) {
      return t('group.min-entities-error', { min: MIN_ENTITIES });
    }
    if (selected.length > MAX_ENTITIES) {
      return t('group.max-entities-error', { max: MAX_ENTITIES });
    }
    return '';
  };

  const handleEntityToggle = (organisationId: string) => {
    setSelectedEntities((prev) => {
      let newSelection: string[];
      if (prev.includes(organisationId)) {
        newSelection = prev.filter((id) => id !== organisationId);
      } else {
        // Don't allow selecting more than MAX_ENTITIES
        if (prev.length >= MAX_ENTITIES) {
          setEntityError(t('group.max-entities-error', { max: MAX_ENTITIES }));
          return prev;
        }
        newSelection = [...prev, organisationId];
      }
      // Validate and update error
      const error = validateEntitySelection(newSelection);
      setEntityError(error);
      return newSelection;
    });
  };

  const handleSubmit = () => {
    // Validate entity selection first
    const entityValidationError = validateEntitySelection(selectedEntities);
    if (entityValidationError) {
      setEntityError(entityValidationError);
      return;
    }

    form
      .validateFields()
      .then((values) => {
        const groupData = {
          groupName: values.groupName,
          country: values.country,
          currency: values.currency,
          vatNumber: values.vatNumber,
          organisationIds: selectedEntities,
        };

        if (editGroup?.organisationId) {
          dispatch(
            updateEntityGroupRequest(editGroup.organisationId, groupData)
          );
          return;
        }

        dispatch(createEntityGroupRequest(groupData));
      })
      .catch(() => {
        // Keep drawer open and let Form.Item messages guide the user.
      });
  };

  const handleCancel = () => {
    dispatch(turnDrawerOff());
  };

  const groupConnectedMembers = useMemo(
    () =>
      editGroup
        ? buildMembersFromEntityList(
            getGroupMemberConnectionIds(editGroup),
            entities,
            t
          )
        : [],
    [editGroup, entities, t]
  );

  const nameAndVatFields = (
    <>
      <LabelWrapper>
        <span className="label-text">{t('group.name')}</span>
      </LabelWrapper>
      <Form.Item<FieldType>
        name="groupName"
        style={{ marginBottom: 12 }}
        rules={[
          {
            required: true,
            message: t('common.required-field'),
          },
        ]}
      >
        <FormInput
          placeholder={t('group.enter-name')}
          width="100%"
          border_color="var(--color-border, #dbeae3)"
        />
      </Form.Item>

      <LabelWrapper>
        <span className="label-text">{t('group.vat-label')}</span>
      </LabelWrapper>
      <Form.Item<FieldType>
        name="vatNumber"
        style={{ marginBottom: 12 }}
        rules={[
          {
            required: true,
            message: t('common.required-field'),
          },
        ]}
      >
        <FormInput
          placeholder={t('group.enter-vat')}
          width="100%"
          border_color="var(--color-border, #dbeae3)"
        />
      </Form.Item>
    </>
  );

  const entitySelectionBlock = !editGroup ? (
    <>
      <LabelWrapper>
        <span className="label-text">{t('group.select-entities')}</span>
      </LabelWrapper>
      <EntityListContainer>
        <RoundInput
          placeholder={t('common.search')}
          prefix={<SearchOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
          bg_color="var(--color-surface-raised, #ffffff)"
          style={{ width: '100%', marginBottom: 8 }}
        />
        <EntityListScrollArea
          tabIndex={0}
          role="region"
          aria-label={t('group.select-entities')}
        >
          {filteredEntities && filteredEntities.length > 0 ? (
            filteredEntities.map((entity) => {
              return (
                <EntityItem
                  key={entity.organisationId}
                  $isSelected={selectedEntities.includes(entity.organisationId)}
                  onClick={() => handleEntityToggle(entity.organisationId)}
                >
                  <EntityInfo>
                    <ProviderIcon
                      providerName={entity?.providerName}
                      alt={entity?.providerName}
                      width={20}
                      style={{ flexShrink: 0 }}
                    />
                    <EntityDetails>
                      <EntityNameBlock>
                        <Text
                          ellipsis={{ tooltip: entity.organisation }}
                          style={{
                            fontSize: '13px',
                            display: 'block',
                            width: '100%',
                          }}
                        >
                          {entity.organisation}
                        </Text>
                      </EntityNameBlock>
                      {entity.vatNumber && (
                        <Text
                          ellipsis={{ tooltip: entity.vatNumber }}
                          style={{
                            fontSize: '11px',
                            color: 'var(--color-text-muted, #5a6a6a)',
                            lineHeight: '14px',
                            display: 'block',
                            maxWidth: '100%',
                          }}
                        >
                          {entity.vatNumber}
                        </Text>
                      )}
                    </EntityDetails>
                  </EntityInfo>
                  <ScopeCheckbox
                    style={{ flexShrink: 0 }}
                    checked={selectedEntities.includes(entity.organisationId)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => handleEntityToggle(entity.organisationId)}
                  />
                </EntityItem>
              );
            })
          ) : (
            <Text
              style={{
                color: 'var(--color-text-muted, #5a6a6a)',
                padding: '20px',
                display: 'block',
                textAlign: 'center',
              }}
            >
              {searchTerm
                ? t('organisation.no-entities-found-title')
                : t('group.no-entities')}
            </Text>
          )}
        </EntityListScrollArea>
      </EntityListContainer>
      <SelectedCount>
        {t('group.selected-count', { count: selectedEntities.length })} (
        {MIN_ENTITIES}-{MAX_ENTITIES} {t('group.required')})
      </SelectedCount>
      <EntityError $visible={Boolean(entityError)}>
        {entityError || '\u00A0'}
      </EntityError>
    </>
  ) : null;

  const groupDetailsReadOnly = editGroup ? (
    <GroupConnectedEntitiesList members={groupConnectedMembers} />
  ) : null;

  const countryAndCurrencyRow = (
    <Row gutter={16}>
      <Col span={12}>
        <LabelWrapper>
          <span className="label-text">{t('common.country')}</span>
        </LabelWrapper>
        <Form.Item<FieldType>
          name="country"
          style={{ marginBottom: 12 }}
          rules={[
            {
              required: true,
              message: t('common.required-field'),
            },
          ]}
        >
          <FormSelect
            // @ts-ignore
            width="100%"
            height="45px"
            placeholder={t('common.select')}
            onChange={onCountryChange}
            bordercolor="var(--color-border, #dbeae3)"
            bgcolor="var(--color-surface-raised, #FFFFFF)"
            disabled={selectedEntities.length < MIN_ENTITIES}
            showSearch
            filterOption={(input, option) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
          >
            {availableCountries &&
              availableCountries.map((c: GroupCountry) => (
                <Select.Option
                  key={c.countryCode}
                  value={c.countryCode}
                  label={getCountrySearchLabel(c)}
                >
                  {renderCountryWithFlag(c)}
                </Select.Option>
              ))}
          </FormSelect>
        </Form.Item>
      </Col>
      <Col span={12}>
        <LabelWrapper>
          <span className="label-text">{t('common.currency')}</span>
        </LabelWrapper>
        <Form.Item<FieldType>
          name="currency"
          style={{ marginBottom: 12 }}
          rules={[
            {
              required: true,
              message: t('common.required-field'),
            },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                if (availableCurrencies.includes(value)) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('common.required-field')));
              },
            },
          ]}
        >
          <FormSelect
            // @ts-ignore
            width="100%"
            height="45px"
            placeholder={t('common.select')}
            onChange={onCurrencyChange}
            bordercolor="var(--color-border, #dbeae3)"
            bgcolor="var(--color-surface-raised, #FFFFFF)"
            disabled={selectedEntities.length < MIN_ENTITIES}
            showSearch
            filterOption={(input, option) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
          >
            {availableCurrencies.map((currency) => (
              <Select.Option key={currency} value={currency} label={currency}>
                {currency}
              </Select.Option>
            ))}
          </FormSelect>
        </Form.Item>
      </Col>
    </Row>
  );

  const formFooter = (
    <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
      <Row gutter={12}>
        <Col span={12}>
          <ScopeButton
            type="default"
            onClick={handleCancel}
            // @ts-ignore
            width="100%"
          >
            {t('common.cancel')}
          </ScopeButton>
        </Col>
        <Col span={12}>
          <ScopeButton
            type="primary"
            onClick={handleSubmit}
            // @ts-ignore
            width="100%"
          >
            {t('common.save')}
          </ScopeButton>
        </Col>
      </Row>
    </Form.Item>
  );

  return (
    <Row justify={'center'}>
      <Col span={24}>
        <Form
          name="createGroup"
          form={form}
          autoComplete="off"
          layout="vertical"
        >
          {editGroup ? (
            <ScopeTabs
              defaultActiveKey="group-details"
              items={[
                {
                  key: 'group-details',
                  label: t('group.group-details'),
                  children: <>{groupDetailsReadOnly}</>,
                },
                {
                  key: 'group-settings',
                  label: t('group.group-settings'),
                  children: (
                    <>
                      {nameAndVatFields}
                      {countryAndCurrencyRow}
                      {formFooter}
                    </>
                  ),
                },
              ]}
            />
          ) : (
            <>
              {nameAndVatFields}
              {entitySelectionBlock}
              {countryAndCurrencyRow}
              {formFooter}
            </>
          )}
        </Form>
      </Col>
    </Row>
  );
};

